import type { ReactNode } from 'react';
import { v4 as uuid } from 'uuid';

import type { MemberInfo, Organization } from './type';

export class OrganizationTreeNode {
  public leaf: boolean;
  public value?: string;
  public user?: Partial<MemberInfo>;
  public key: string;
  public childNodes: OrganizationTreeNode[] = [];
  public parentNode?: OrganizationTreeNode;
  public isUser: boolean;

  public constructor({ leaf, value, user }: Organization) {
    this.leaf = leaf;
    this.value = value;
    this.user = user;
    this.isUser = false;
    this.key = this.user
      ? this.user.pernr !== undefined
        ? this.user.pernr.toString()
        : uuid()
      : uuid();
  }

  public get hasChildren() {
    return this.childNodes.length !== 0;
  }

  public get title() {
    return this.user ? `${this?.user?.nameEn}-${this.user?.nameZh}` : this.value || '';
  }

  public get completeName(): string {
    return this.user
      ? `${this?.user?.nameEn}-${this.user?.nameZh}`
      : `${this.parentNode ? `${this.parentNode.completeName}-` : ''}${this.value}`;
  }

  public get parentNodes() {
    const parentNodes: OrganizationTreeNode[] = [];

    if (this.parentNode) {
      parentNodes.push(...[...this.parentNode.parentNodes, this.parentNode]);
    }

    return parentNodes;
  }

  public get parentKeyList() {
    const parentKeyList: string[] = [];

    if (this.parentNode) {
      parentKeyList.push(...[...this.parentNode.parentKeyList, this.parentNode.key]);
    }

    return parentKeyList;
  }

  public get treeData(): {
    key: string;
    title: ReactNode;
    completeName: string;
    isUser: boolean;
    children?: OrganizationTreeNode['treeData'][];
  } {
    return {
      isUser: !!this.user,
      key: this.key,
      title: this.title,
      completeName: this.completeName,
      children: this.childNodes.map((childNode) => childNode.treeData),
    };
  }

  public setParent(parentNode: OrganizationTreeNode) {
    this.parentNode = parentNode;
  }

  public setChildren(childNodes: OrganizationTreeNode[]) {
    this.childNodes = childNodes;
  }
}

export class OrganizationDataTree {
  public nodes: OrganizationTreeNode[] = [];
  private nodesMap = new Map<string, OrganizationTreeNode>();

  public constructor(nodes: Organization[]) {
    const traverse = (tempNodes: Organization[], parentNode?: OrganizationTreeNode) => {
      return tempNodes
        .filter((node) => {
          if (node.user) {
            return node.user.pernr != null;
          }
          return true;
        })
        .map((node) => {
          const treeNode = new OrganizationTreeNode(node);
          parentNode && treeNode.setParent(parentNode);
          this.nodesMap.set(treeNode.key, treeNode);
          if (node.children) {
            treeNode.setChildren(traverse(node.children, treeNode));
          }

          return treeNode;
        });
    };
    this.nodes = traverse(nodes);
  }

  public get data() {
    return this.nodes.map((node) => node.treeData);
  }

  public getNodesById(id: string) {
    return [...this.nodesMap.values()].filter((node) => node.value === id);
  }

  public getNode(key: string) {
    return this.nodesMap.get(key);
  }
}
